import { useEffect, useRef, useState, DragEvent, ChangeEvent } from 'react';
import {
  SprayCan,
  Upload,
  Loader2,
  Download,
  AlertCircle,
  CheckCircle,
  FileText,
  Sparkles,
  Settings,
  X,
  Plus,
  FlaskConical,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  dataWashPrepare,
  dataWashWash,
  dataWashWashStart,
  dataWashWashStatus,
  dataWashWashCancel,
  dataWashBuild,
  dataWashLearn,
  type WashMode,
  type PrepareResult,
  type WashResult,
  type WashJobStatus,
  type LearnProposal,
  type LearnResult,
} from '../api/tools';
import { createPiiRule, testPiiPattern, type PiiPatternTestResult } from '../api/admin';
import { useToastStore } from '../stores/toast';
import { useAuthStore } from '../stores/auth';
import PiiRulesTab from '../components/admin/PiiRulesTab';

type Phase = 'idle' | 'preparing' | 'prepared' | 'washing' | 'building' | 'done' | 'error';
type OutputFormat = 'pdf' | 'docx' | 'txt';

const CATEGORY_LABELS: Record<string, string> = {
  ssn: 'SSN',
  email: 'Email',
  phone: 'Phone',
  credit_card: 'Credit Card',
  isin: 'ISIN/CUSIP',
  account_number: 'Account Number',
  dob: 'Date of Birth',
  date: 'Date',
  address: 'Address',
  zip: 'ZIP Code',
  name: 'Name',
  signature: 'Signature',
  secret: 'Custom Rule',
};

const WASH_MODES: { mode: WashMode; label: string; hint: string }[] = [
  {
    mode: 'patterns',
    label: 'Pattern redaction (PII Rules)',
    hint: 'Redacts PII using the admin PII Rules plus the hardcoded fallback.',
  },
  {
    mode: 'ai',
    label: 'AI clean (local)',
    hint: 'Uses the local AI to rewrite the document removing PII. Stays on-site; nothing sent to the cloud.',
  },
  {
    mode: 'none',
    label: 'No redaction',
    hint: 'The document is still cleaned and rebuilt from text, but PII is not redacted.',
  },
];

const ACCEPT = '.pdf,.docx,.txt,.text,.html,.xml,.json,.rtf,.csv,.md';

export default function DataWash() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState('');
  const [extractedText, setExtractedText] = useState('');
  const [piiDetected, setPiiDetected] = useState<Record<string, number>>({});
  const [fileName, setFileName] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [washMode, setWashMode] = useState<WashMode>('patterns');
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('pdf');
  const [lastSummary, setLastSummary] = useState<WashResult['summary'] | null>(null);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [downloadName, setDownloadName] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  // Async AI-wash progress (huge docs run many minutes): live chunk counter +
  // a Cancel button. washJobIdRef lets the Cancel handler reach the job.
  const [washProgress, setWashProgress] = useState<{ completed: number; total: number; lastDur: number | null } | null>(null);
  const [washCancelling, setWashCancelling] = useState(false);
  const washJobIdRef = useRef<string | null>(null);
  // Cache washed text per mode so format changes don't re-wash (esp. AI).
  const washedByModeRef = useRef<Record<string, string>>({});
  const summaryByModeRef = useRef<Record<string, WashResult['summary']>>({});
  const didBuildRef = useRef(false);

  // Learn-from-document + Edit-PII-Rules popup.
  const addToast = useToastStore((s) => s.addToast);
  const { user } = useAuthStore();
  // PII Rules management (edit + train on documents) is admin/operator + principal only.
  const canManagePii = user?.role === 'operator' || user?.role === 'principal';
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [learning, setLearning] = useState(false);
  const [learnError, setLearnError] = useState('');
  const [learnResult, setLearnResult] = useState<LearnResult | null>(null);
  const [addedNames, setAddedNames] = useState<Set<string>>(new Set());
  const [addingName, setAddingName] = useState<string | null>(null);
  const [testOut, setTestOut] = useState<Record<string, PiiPatternTestResult | null>>({});
  const [testingName, setTestingName] = useState<string | null>(null);
  const [editRulesOpen, setEditRulesOpen] = useState(false);

  useEffect(() => {
    return () => {
      // revoke any object URL on unmount
      setDownloadUrl((url) => {
        if (url) URL.revokeObjectURL(url);
        return '';
      });
    };
  }, []);

  const reset = () => {
    setPhase('idle');
    setError('');
    setExtractedText('');
    setPiiDetected({});
    setFileName('');
    setCharCount(0);
    setLastSummary(null);
    if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    setDownloadUrl('');
    setDownloadName('');
    setShowPreview(false);
    washedByModeRef.current = {};
    summaryByModeRef.current = {};
    didBuildRef.current = false;
    setSelectedFile(null);
    setLearning(false);
    setLearnError('');
    setLearnResult(null);
    setAddedNames(new Set());
    setAddingName(null);
    setTestOut({});
    setTestingName(null);
  };

  const handleFile = async (file: File) => {
    reset();
    setFileName(file.name);
    setSelectedFile(file);
    setPhase('preparing');
    try {
      const result: PrepareResult = await dataWashPrepare(file);
      setExtractedText(result.extracted_text);
      setPiiDetected(result.pii_detected || {});
      setCharCount(result.char_count);
      setFileName(result.filename || file.name);
      setPhase('prepared');
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Prepare request failed.';
      setError(msg);
      setPhase('error');
    }
  };

  // Async AI wash: start a job, poll for live chunk progress, honor cancel.
  // Returns the washed text. Throws on error/cancel.
  const runAiWash = async (): Promise<string> => {
    setWashCancelling(false);
    const start = await dataWashWashStart(extractedText);
    washJobIdRef.current = start.job_id;
    setWashProgress({ completed: 0, total: start.total_chunks, lastDur: null });
    let st: WashJobStatus = await dataWashWashStatus(start.job_id);
    while (st.status === 'running') {
      setWashProgress({ completed: st.completed_chunks, total: st.total_chunks, lastDur: st.last_chunk_duration_s ?? null });
      await new Promise((r) => setTimeout(r, 1000));
      st = await dataWashWashStatus(start.job_id);
    }
    setWashProgress({ completed: st.completed_chunks, total: st.total_chunks, lastDur: st.last_chunk_duration_s ?? null });
    washJobIdRef.current = null;
    if (st.status === 'complete' && st.washed_text != null) {
      setWashProgress(null);
      return st.washed_text;
    }
    setWashProgress(null);
    throw new Error(st.status === 'cancelled' ? 'AI wash cancelled.' : (st.error || 'AI wash failed.'));
  };

  // User-initiated cancel of a running AI wash.
  const cancelWash = async () => {
    const jobId = washJobIdRef.current;
    if (!jobId) return;
    setWashCancelling(true);
    try {
      await dataWashWashCancel(jobId);
    } catch {
      // The polling loop picks up the cancelled status regardless.
    }
  };

  // Produce a download: ensure the washed text for the current mode is
  // cached (washing is the expensive step -- AI can take many minutes), then
  // build the doc from it. Format changes after this reuse the cache.
  const handleDownload = async () => {
    if (!extractedText) return;
    let washed = washedByModeRef.current[washMode];
    if (washed == null) {
      setPhase('washing');
      try {
        if (washMode === 'ai') {
          // Async: start job + poll for progress (UI shows Chunk x/N + Cancel).
          washed = await runAiWash();
        } else {
          const result = await dataWashWash(extractedText, washMode);
          washed = result.washed_text;
          summaryByModeRef.current[washMode] = result.summary;
          setLastSummary(result.summary);
        }
        washedByModeRef.current[washMode] = washed;
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : 'Wash request failed.';
        setError(msg);
        setPhase('error');
        return;
      }
    } else {
      setLastSummary(summaryByModeRef.current[washMode] ?? null);
    }
    await buildAndOffer(washed);
  };

  const buildAndOffer = async (washed: string) => {
    setPhase('building');
    try {
      const result = await dataWashBuild(washed, outputFormat);
      if (downloadUrl) URL.revokeObjectURL(downloadUrl);
      const url = URL.createObjectURL(result.blob);
      setDownloadUrl(url);
      // Preserve the ORIGINAL upload's base name (the build endpoint only
      // knows the washed text, so it returns a generic "document_DATAWASHED"
      // name; rebuild the name here from the uploaded filename + format).
      const base = (fileName || 'document').replace(/\.[^.]+$/, '');
      setDownloadName(`${base}_DATAWASHED.${outputFormat}`);
      didBuildRef.current = true;
      setPhase('done');
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Build request failed.';
      setError(msg);
      setPhase('error');
    }
  };

  // When the output format changes AFTER a build has already produced a
  // download, re-BUILD from the cached washed text (cheap -- no re-wash).
  // This replaces the old bundled auto-re-wash effect.
  const didMountFmtRef = useRef(false);
  useEffect(() => {
    if (!didMountFmtRef.current) {
      didMountFmtRef.current = true;
      return;
    }
    if (!didBuildRef.current) return;
    const cached = washedByModeRef.current[washMode];
    if (cached != null) {
      void buildAndOffer(cached);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputFormat]);

  // Auto-trigger the browser download whenever a freshly built doc is ready.
  // handleDownload (and the format-change rebuild) set downloadUrl; this
  // effect turns that blob URL into an actual file save. There is no static
  // <a download> link in the UI -- the button drives the flow programmatically.
  useEffect(() => {
    if (!downloadUrl || !downloadName) return;
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [downloadUrl, downloadName]);

  // Wash-mode change: clear the cached download link (the washed text for
  // the new mode may not be cached yet). Do NOT auto-wash (AI is slow) --
  // let the user click Download to trigger the wash.
  const onWashModeChange = (mode: WashMode) => {
    if (mode === washMode) return;
    setWashMode(mode);
    if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    setDownloadUrl('');
    setDownloadName('');
    didBuildRef.current = false;
    if (washedByModeRef.current[mode] != null) {
      setLastSummary(summaryByModeRef.current[mode] ?? null);
    } else {
      setLastSummary(null);
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  // Learn from document -> LOCAL LLM proposes new PII rules.
  const handleLearn = async () => {
    if (!selectedFile) return;
    setLearning(true);
    setLearnError('');
    setLearnResult(null);
    setAddedNames(new Set());
    setTestOut({});
    try {
      const result = await dataWashLearn(selectedFile, 10);
      setLearnResult(result);
      if (result.proposals.length === 0) {
        addToast('No new PII patterns proposed (all already covered).', 'info');
      } else {
        addToast(`Proposed ${result.proposals.length} new PII rule(s).`, 'success');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Learn request failed.';
      setLearnError(msg);
      addToast(msg, 'error');
    } finally {
      setLearning(false);
    }
  };

  const proposalKey = (p: LearnProposal) => `${p.name}::${p.pattern}`;

  const handleAddRule = async (p: LearnProposal) => {
    const key = proposalKey(p);
    setAddingName(key);
    try {
      await createPiiRule({
        name: p.name,
        category: p.category,
        pattern: p.pattern,
        replacement: '[REDACTED]',
        enabled: true,
        priority: 0,
        group_index: 0,
      });
      setAddedNames(new Set([...addedNames, key]));
      addToast(`Added PII rule "${p.name}"`, 'success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to add rule.';
      addToast(msg, 'error');
    } finally {
      setAddingName(null);
    }
  };

  const handleAddAllValid = async () => {
    const valid = learnResult?.proposals.filter((p) => p.valid) ?? [];
    for (const p of valid) {
      const key = proposalKey(p);
      if (!addedNames.has(key)) await handleAddRule(p);
    }
  };

  const handleTestProposal = async (p: LearnProposal) => {
    const key = proposalKey(p);
    setTestingName(key);
    try {
      const sampleText = (p.examples && p.examples.length > 0)
        ? p.examples.join('\n')
        : p.pattern;
      const out = await testPiiPattern({
        pattern: p.pattern,
        text: sampleText,
        group_index: 0,
        replacement: '[REDACTED]',
      });
      setTestOut({ ...testOut, [key]: out });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Pattern test failed.';
      addToast(msg, 'error');
    } finally {
      setTestingName(null);
    }
  };

  const detectedCounts = Object.entries(piiDetected).sort((a, b) => b[1] - a[1]);
  const summaryCounts = (() => {
    if (lastSummary && 'ai_wash' in lastSummary) return [];
    if (lastSummary) {
      return Object.entries(lastSummary as Record<string, number>)
        .sort((a, b) => b[1] - a[1]);
    }
    return [];
  })();
  const isAiSummary = !!(lastSummary && 'ai_wash' in lastSummary);
  const busy = phase === 'preparing' || phase === 'washing' || phase === 'building';
  const hasCachedForMode = washedByModeRef.current[washMode] != null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Data Wash</h1>
        <p className="text-sm text-slate-500 mt-1">
          Rebuild a clean, redacted document from your upload.
        </p>
      </div>

      <section className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <SprayCan className="text-sky-600" size={20} />
            <h2 className="text-lg font-semibold text-slate-900">
              Data Wash
            </h2>
            {canManagePii && (
              <button
                onClick={() => setEditRulesOpen(true)}
                className="ml-auto inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                title="Open the PII Rules editor (add/edit/test/AI-generate rules)"
              >
                <Settings size={16} />
                PII Rules
              </button>
            )}
          </div>
          <p className="text-sm text-slate-600 mt-2">
            Data Wash extracts the text from your upload, then rebuilds a
            clean document from that text on download. PII is redacted
            using the rules you choose. The output contains only the
            (optionally redacted) text.
          </p>
          <p className="text-xs text-slate-500 mt-2">
            The rebuilt document keeps the text content only &mdash; original
            formatting (fonts, layout, images, tables, colors, and headers)
            is not carried over. This is intentional: it ensures no hidden
            metadata, embedded objects, or signatures travel with the file.
          </p>
          {canManagePii && (
            <p className="text-xs text-slate-500 mt-2">
              Admin: after uploading, use <strong>Learn from this document</strong> to train PII rules on the document’s content, or <strong>PII Rules</strong> to edit rules.
            </p>
          )}
        </div>

        <div className="p-5 space-y-4">
          {/* Drop zone */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className="cursor-pointer rounded-lg border-2 border-dashed border-slate-300 hover:border-sky-400 hover:bg-sky-50/40 transition-colors py-10 flex flex-col items-center justify-center text-slate-500"
          >
            <Upload size={28} className="text-slate-400" />
            <p className="mt-2 text-sm font-medium text-slate-700">
              Drag &amp; drop a document here, or click to browse
            </p>
            <p className="text-xs text-slate-400 mt-1">
              PDF, DOCX, TXT, HTML, XML, JSON, RTF (up to 25 MB)
            </p>
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              onChange={onPick}
              accept={ACCEPT}
            />
          </div>

          {phase === 'preparing' && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Loader2 className="animate-spin" size={18} />
              <span>Extracting text from &ldquo;{fileName}&rdquo;&hellip;</span>
            </div>
          )}

          {phase === 'error' && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              <AlertCircle size={18} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Data wash failed</p>
                <p className="text-xs">{error}</p>
              </div>
            </div>
          )}

          {/* Prepared state: show what was extracted + controls */}
          {(phase === 'prepared' || phase === 'washing' || phase === 'building' || phase === 'done') && (
            <div className="space-y-4">
              {/* Extracted summary */}
              <div className="rounded-md border border-slate-200">
                <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
                  <FileText size={14} className="text-slate-500" />
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                    {fileName} &middot; {charCount.toLocaleString()} chars
                  </p>
                  <button
                    onClick={() => setShowPreview((v) => !v)}
                    className="ml-auto text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                  >
                    {showPreview ? <>Hide text <ChevronUp size={13} /></> : <>Show text <ChevronDown size={13} /></>}
                  </button>
                </div>
                <div className="p-3 space-y-2">
                  <p className="text-xs font-medium text-slate-600">
                    PII detected (before washing):
                  </p>
                  {detectedCounts.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {detectedCounts.map(([cat, n]) => (
                        <span
                          key={cat}
                          className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-xs font-medium text-amber-800"
                        >
                          {CATEGORY_LABELS[cat] || cat}
                          <span className="text-amber-500">x{n}</span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">
                      No PII regions detected by the rules.
                    </p>
                  )}
                  {showPreview && (
                    <pre className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1 max-h-48 overflow-auto whitespace-pre-wrap break-words">
                      {extractedText.slice(0, 4000)}
                      {extractedText.length > 4000 ? '\n[...]' : ''}
                    </pre>
                  )}
                </div>
              </div>

              {/* Wash mode selector */}
              <div>
                <span className="block text-sm font-medium text-slate-700 mb-1">
                  Wash mode
                </span>
                <div className="flex flex-col gap-2">
                  {WASH_MODES.map((m) => (
                    <label
                      key={m.mode}
                      className={`cursor-pointer rounded-lg border px-3 py-2 text-sm transition-colors ${
                        washMode === m.mode
                          ? 'border-sky-500 bg-sky-50'
                          : 'border-slate-300 hover:border-slate-400'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="dw-washmode"
                          value={m.mode}
                          checked={washMode === m.mode}
                          onChange={() => onWashModeChange(m.mode)}
                          disabled={busy}
                          className="hidden"
                        />
                        <span className={`font-medium ${washMode === m.mode ? 'text-sky-700' : 'text-slate-700'}`}>
                          {m.label}
                        </span>
                        {washMode === m.mode && hasCachedForMode && (
                          <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">cached</span>
                        )}
                      </div>
                      <span className="block text-xs text-slate-500 mt-0.5">{m.hint}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Output format selector */}
              <div>
                <span className="block text-sm font-medium text-slate-700 mb-1">
                  Output format
                </span>
                <div className="flex gap-2">
                  {(['pdf', 'docx', 'txt'] as OutputFormat[]).map((fmt) => (
                    <label
                      key={fmt}
                      className={`cursor-pointer rounded-lg border px-3 py-1.5 text-sm font-medium uppercase transition-colors ${
                        outputFormat === fmt
                          ? 'border-sky-500 bg-sky-50 text-sky-700'
                          : 'border-slate-300 text-slate-600 hover:border-slate-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="dw-format"
                        value={fmt}
                        checked={outputFormat === fmt}
                        onChange={() => setOutputFormat(fmt)}
                        disabled={busy}
                        className="hidden"
                      />
                      {fmt}
                    </label>
                  ))}
                </div>
              </div>

              {phase === 'washing' && (
                <div className="flex flex-col gap-2 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <Loader2 className="animate-spin" size={18} />
                    <span>
                      {washProgress
                        ? `Cleaning with AI (local) - chunk ${washProgress.completed} / ${washProgress.total}` +
                          (washProgress.lastDur != null ? ` · last call ${washProgress.lastDur}s` : '') +
                          (washCancelling ? ' - cancelling...' : '')
                        : washMode === 'ai'
                          ? 'Cleaning with AI (local) - this can take many minutes on a large document...'
                          : 'Washing document...'}
                    </span>
                  </div>
                  {washProgress && (
                    <div className="flex items-center gap-3">
                      <div className="h-2 flex-1 max-w-xs rounded-full bg-slate-200 overflow-hidden">
                        <div
                          className="h-full bg-indigo-500 transition-all"
                          style={{
                            width: `${washProgress.total ? Math.round((washProgress.completed / washProgress.total) * 100) : 0}%`,
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={cancelWash}
                        disabled={washCancelling}
                        className="inline-flex items-center gap-1 rounded-md border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        <X size={13} /> {washCancelling ? 'Cancelling...' : 'Cancel'}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {phase === 'building' && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Loader2 className="animate-spin" size={18} />
                  <span>Building {outputFormat.toUpperCase()}...</span>
                </div>
              )}

              {phase === 'done' && (
                <div className="flex items-start gap-2 rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700">
                  <CheckCircle size={18} className="mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">Washed document ready</p>
                    <p className="text-xs">
                      A clean document is ready to download.
                    </p>
                  </div>
                </div>
              )}

              {/* Wash summary */}
              {lastSummary && (
                <div className="rounded-md border border-slate-200">
                  <div className="px-3 py-2 border-b border-slate-200 bg-slate-50">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                      Wash summary
                    </p>
                  </div>
                  <div className="p-3">
                    {isAiSummary ? (
                      <p className="text-xs text-slate-600">
                        AI-cleaned (local). PII replaced with placeholders by the model.
                      </p>
                    ) : summaryCounts.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {summaryCounts.map(([cat, n]) => (
                          <span
                            key={cat}
                            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
                          >
                            {CATEGORY_LABELS[cat] || cat}
                            <span className="text-slate-400">x{n}</span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500">
                        No PII regions redacted (none detected / redaction off).
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Download + Learn + Edit PII Rules */}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={handleDownload}
                  disabled={busy}
                  className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50 transition-colors"
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                  {downloadUrl ? `Download ${outputFormat.toUpperCase()}` : `Wash & download ${outputFormat.toUpperCase()}`}
                </button>
                {canManagePii && (
                <button
                  onClick={handleLearn}
                  disabled={learning || !selectedFile}
                  className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition-colors"
                  title="Send the document text to the local AI to propose new PII rules"
                >
                  {learning ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                  Learn from this document
                </button>
                )}
                {canManagePii && (
                <button
                  onClick={() => setEditRulesOpen(true)}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <Settings size={16} />
                  Edit PII Rules
                </button>
                )}
                {downloadUrl && (
                  <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                    <FileText size={14} />
                    {downloadName}
                  </span>
                )}
                <button
                  onClick={reset}
                  className="ml-auto text-sm text-slate-500 hover:text-slate-700"
                >
                  Wash another
                </button>
              </div>

              {/* Hint when mode changed but not yet re-washed */}
              {!hasCachedForMode && washMode !== 'patterns' && phase !== 'washing' && !downloadUrl && (
                <p className="text-xs text-slate-500">
                  Click <strong>Wash &amp; download</strong> to wash with the new mode.
                </p>
              )}

              {/* Learn error */}
              {learnError && (
                <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  <AlertCircle size={18} className="mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">Learn failed</p>
                    <p className="text-xs">{learnError}</p>
                  </div>
                </div>
              )}

              {/* Proposed new PII rules */}
              {learnResult && (
                <div className="rounded-md border border-emerald-200">
                  <div className="px-3 py-2 border-b border-emerald-200 bg-emerald-50 flex items-center gap-2">
                    <Sparkles size={14} className="text-emerald-700" />
                    <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">
                      Proposed new PII rules
                    </p>
                    {learnResult.proposals.some((p) => p.valid) && (
                      <button
                        onClick={handleAddAllValid}
                        className="ml-auto text-xs font-medium text-emerald-700 hover:text-emerald-900"
                      >
                        Add all valid
                      </button>
                    )}
                  </div>
                  <div className="p-3 space-y-3">
                    {learnResult.proposals.length === 0 && (
                      <p className="text-xs text-slate-500">
                        No new PII patterns proposed &mdash; everything in this
                        document appears to be already covered by the existing
                        rules{learnResult.skipped_invalid > 0
                          ? ` (${learnResult.skipped_invalid} invalid proposal(s) skipped)`
                          : ''}.
                      </p>
                    )}
                    {learnResult.proposals.map((p) => {
                      const key = `${p.name}::${p.pattern}`;
                      const added = addedNames.has(key);
                      const testing = testingName === key;
                      const adding = addingName === key;
                      const tOut = testOut[key];
                      return (
                        <div key={key} className="rounded-md border border-slate-200 bg-white p-3 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-800">{p.name}</span>
                            <span className="text-[10px] font-bold uppercase tracking-wide bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{p.category}</span>
                            {p.valid ? (
                              <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">valid regex</span>
                            ) : (
                              <span className="text-[10px] font-bold uppercase tracking-wide bg-red-100 text-red-800 px-1.5 py-0.5 rounded">invalid</span>
                            )}
                            <div className="ml-auto flex items-center gap-2">
                              <button
                                onClick={() => handleTestProposal(p)}
                                disabled={testing || !p.valid}
                                className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-800 disabled:opacity-50"
                              >
                                {testing ? <Loader2 size={13} className="animate-spin" /> : <FlaskConical size={13} />}
                                Test
                              </button>
                              <button
                                onClick={() => handleAddRule(p)}
                                disabled={adding || added || !p.valid}
                                className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900 disabled:opacity-50"
                              >
                                {adding ? <Loader2 size={13} className="animate-spin" /> : added ? <CheckCircle size={13} /> : <Plus size={13} />}
                                {added ? 'Added' : 'Add rule'}
                              </button>
                            </div>
                          </div>
                          <pre className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 font-mono overflow-x-auto">{p.pattern}</pre>
                          {p.examples.length > 0 && (
                            <p className="text-xs text-slate-500">
                              <span className="font-medium text-slate-600">Examples from doc:</span>{' '}
                              {p.examples.join(', ')}
                            </p>
                          )}
                          {tOut && (
                            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1">
                              <span className="font-medium">Test:</span> {tOut.match_count} match(es) &mdash;{' '}
                              <span className="font-mono">{tOut.redacted_text}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {learnResult.skipped_invalid > 0 && (
                      <p className="text-xs text-slate-400">
                        {learnResult.skipped_invalid} invalid proposal(s) skipped (regex did not compile).
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Edit PII Rules modal: reuse PiiRulesTab inline. */}
      {editRulesOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm animate-fade-in"
          onClick={() => setEditRulesOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Settings className="text-sky-600" size={18} />
                <h2 className="text-lg font-semibold text-slate-800">Edit PII Rules</h2>
              </div>
              <button
                onClick={() => setEditRulesOpen(false)}
                className="text-slate-400 hover:text-slate-600"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-4">
              <PiiRulesTab embedded />
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
              <button
                onClick={() => setEditRulesOpen(false)}
                className="rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}